/**
 * 講師フィードバック動画録音ユーティリティ（Android/PC のみ対応、iOS非対応）
 * 縦型・低ビットレートで最小データサイズを優先する設定
 * 既存の audioRecorder.js とは完全に独立しており、音声フローには影響しない
 *
 * 生カメラstreamをそのままMediaRecorderに渡して録画する。以前はcanvasに毎フレーム
 * 描画し直してから録画していたが、その処理パイプラインが音声より遅れて出てくるせいで
 * 音ズレの原因になっていたため廃止した。向き・アスペクト比の補正は全てサーバー側の
 * ffmpegで行う（講師が「向きを直す」ボタンで確定した角度をそのまま使うため、
 * 推測に頼らず正確に補正できる）。プレビューの見た目だけはCSSで回転させる。
 */

// 対応コーデックの優先順位（webm/vp9 を優先、Safari系は対象外）
const VIDEO_CODEC_PRIORITIES = [
  'video/webm; codecs=vp9,opus',
  'video/webm; codecs=vp8,opus',
  'video/webm'
]

const VIDEO_BITS_PER_SECOND = 700000   // 700kbps（480x640相当・20fps想定）
const AUDIO_BITS_PER_SECOND = 40000    // 40kbps（会話音声のみ）

export const VIDEO_RECORDING_LIMITS = {
  timeslice: 6000,           // 6秒チャンク
  maxDurationMs: 5 * 60 * 1000,      // ハード上限 5分
  recommendedMaxMs: 3 * 60 * 1000    // 運用上の推奨上限 3分（UI表示用）
}

const ROTATION_STORAGE_PREFIX = 'videoFeedback.rotationDeg:'

/**
 * 利用可能な動画コーデックを検出
 * @returns {Object|null} 対応コーデックが無い場合は null
 */
export function detectOptimalVideoCodec() {
  for (const codec of VIDEO_CODEC_PRIORITIES) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(codec)) {
      return {
        mimeType: codec,
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND
      }
    }
  }
  return null
}

/**
 * 動画フィードバック録画が可能な環境かどうか
 * iOSは MediaRecorder の webm 動画録画に対応していないため非対応とする
 */
export function isVideoFeedbackSupported() {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const isIOS = /iPad|iPhone|iPod/.test(userAgent)
  if (isIOS) return false
  if (typeof MediaRecorder === 'undefined') return false
  return detectOptimalVideoCodec() !== null
}

/**
 * カメラ制約を取得。生streamをそのまま録画するため、ここでの解像度指定が
 * そのまま録画解像度になる（サーバー側で3:4にクロップされる前提のヒント値）
 */
export function getVideoConstraints() {
  return {
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      sampleRate: 44100,
      channelCount: 1
    },
    video: {
      facingMode: { ideal: 'user' },
      width: { ideal: 480 },
      height: { ideal: 640 },
      frameRate: { ideal: 20, max: 24 }
    }
  }
}

/**
 * 端末・カメラ解像度ごとに回転補正値を区別して保存するためのキー
 * @param {MediaStream} rawStream
 */
export function getRotationStorageKey(rawStream) {
  const track = rawStream?.getVideoTracks?.()[0]
  const settings = track?.getSettings ? track.getSettings() : {}
  return `${ROTATION_STORAGE_PREFIX}${settings.facingMode || 'unknown'}:${settings.width || 'w'}:${settings.height || 'h'}`
}

/**
 * 端末ローカルに保存された回転補正値を読み込む（無ければnull）
 * @param {MediaStream} rawStream
 * @returns {number|null}
 */
export function loadLocalRotation(rawStream) {
  try {
    const key = getRotationStorageKey(rawStream)
    const saved = window.localStorage.getItem(key)
    if (saved === null) return null
    const deg = Number(saved)
    return [0, 90, 180, 270].includes(deg) ? deg : null
  } catch {
    return null
  }
}

/**
 * 端末ローカルに回転補正値を保存する
 * @param {MediaStream} rawStream
 * @param {number} rotationDeg
 */
export function saveLocalRotation(rawStream, rotationDeg) {
  try {
    const key = getRotationStorageKey(rawStream)
    window.localStorage.setItem(key, String(rotationDeg))
  } catch {
    // localStorageが使えない環境では何もしない（次回もう一度手動補正すればよい）
  }
}

/**
 * カメラの生streamを受け取り、プレビュー・録画兼用のセッションを作る。
 * canvasは経由せず生streamをそのまま使うため、音声・映像は常にブラウザネイティブの
 * タイミングで同期している。向き補正はプレビュー表示（CSSのtransform）だけに留め、
 * 実際に保存される映像の回転・3:4クロップはサーバー側のffmpegで行う
 * （呼び出し側はgetRotation()の値を録画完了時にサーバーへ送ること）
 *
 * @param {MediaStream} rawStream getUserMediaで取得した生stream（音声トラックも含む）
 * @param {number} initialRotationDeg 初期回転角度（0/90/180/270、プレビュー表示用）
 */
export async function createPortraitPreviewSession(rawStream, initialRotationDeg = 0) {
  const videoTrack = rawStream.getVideoTracks()[0]
  const settings = videoTrack?.getSettings ? videoTrack.getSettings() : {}
  const sourceW = settings.width || 0
  const sourceH = settings.height || 0

  let rotationDeg = [0, 90, 180, 270].includes(initialRotationDeg) ? initialRotationDeg : 0

  return {
    // プレビュー・録画兼用stream（生streamそのもの。canvas等の再処理は一切行わない）
    stream: rawStream,
    rawStream,
    sourceInfo: { sourceW, sourceH, isLandscapeSource: sourceW > sourceH },
    getRotation() {
      return rotationDeg
    },
    setRotation(deg) {
      rotationDeg = [0, 90, 180, 270].includes(deg) ? deg : 0
    },
    rotateClockwise() {
      rotationDeg = (rotationDeg + 90) % 360
      return rotationDeg
    },
    stop() {
      rawStream.getTracks().forEach(track => track.stop())
    }
  }
}
